import { z } from "zod";
import type {
  ConditionalRuleDto,
  ConditionalRulesDto,
  ShopOverrideRuleActionDto,
} from "@babackapp/private-api-client/types";

export const conditionalRuleSchema = z.object({
  id: z.string().min(1, "Field is required"),
  type: z.enum([
    "STRING",
    "NUMBER",
    "DATE",
    "ARRAY",
    "BOOLEAN",
    "NUMBER_ARRAY",
    "KEY_VALUE_ARRAY",
  ]),
  operator: z.enum([
    "EQ",
    "NE",
    "GT",
    "GTE",
    "LT",
    "LTE",
    "IN",
    "NOT_IN",
    "BETWEEN",
    "CONTAINS",
    "NOT_CONTAINS",
    "EVERY_EQ",
    "EVERY_NE",
    "EVERY_GT",
    "EVERY_GTE",
    "EVERY_LT",
    "EVERY_LTE",
    "EVERY_BETWEEN",
    "SOME_EQ",
    "SOME_NE",
    "SOME_GT",
    "SOME_GTE",
    "SOME_LT",
    "SOME_LTE",
    "SOME_BETWEEN",
    "NONE_EQ",
    "NONE_NE",
    "NONE_GT",
    "NONE_GTE",
    "NONE_LT",
    "NONE_LTE",
    "NONE_BETWEEN",
    "HAS_KEY_VALUE",
    "NOT_HAS_KEY_VALUE",
  ]),
  value: z
    .union([
      z.string().min(1, "Value is required"),
      z.number(),
      z.boolean(),
      z.array(z.number()),
      z.array(z.string()),
      z.object({ key: z.string(), value: z.string() }),
    ])
    .refine(
      (val) => {
        if (val === null || val === undefined || val === "") return false;
        if (val === false) return true;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      },
      { message: "Value is required" },
    ),
}) satisfies z.ZodType<ConditionalRuleDto>;

export const conditionalRulesSchema = z.object({
  operator: z.enum(["AND", "OR"]),
  rules: z
    .array(conditionalRuleSchema)
    .min(1, "At least one condition is required"),
}) satisfies z.ZodType<ConditionalRulesDto>;

export const actionSchema = z.object({
  type: z.enum([
    "OVERRIDE_AUTO_TREATMENT",
    "OVERRIDE_RETURN_DELAY",
    "OVERRIDE_SHIPPING_REFUND",
    "OVERRIDE_INITIAL_SHIPPING_AMOUNT",
    "OVERRIDE_STORE_RETURN",
    "OVERRIDE_RESHIPPING_PAID",
    "OVERRIDE_CREDIT_ENABLED",
  ]),
  parameters: z
    .record(z.string(), z.unknown())
    .optional()
    .refine(
      (val) => !!val && (val.value === false || val.value === 0 || !!val.value),
      {
        message: "Action value is required",
      },
    ),
}) satisfies z.ZodType<ShopOverrideRuleActionDto>;

export const overrideRuleFormSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name must be less than 100 characters"),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
  priority: z
    .number()
    .int()
    .min(1, "Priority must be at least 1")
    .max(100, "Priority must be less than 100"),
  conditions: conditionalRulesSchema,
  action: actionSchema,
});

export type OverrideRuleFormValuesInput = z.input<
  typeof overrideRuleFormSchema
>;
export type OverrideRuleFormValues = z.infer<typeof overrideRuleFormSchema>;

export const createOverrideRuleSchema = overrideRuleFormSchema;
export const updateOverrideRuleSchema = overrideRuleFormSchema.extend({
  id: z.string(),
});

export type CreateOverrideRuleFormValues = z.infer<
  typeof createOverrideRuleSchema
>;
export type UpdateOverrideRuleFormValues = z.infer<
  typeof updateOverrideRuleSchema
>;
